/** 指揮系統図。上長の連なりを木で出す(手動指定が無い人は役職から自動導出)。 */
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Flex,
  Segmented,
  Space,
  Spin,
  Tag,
  Tree,
  Typography,
} from "antd";
import type { DataNode } from "antd/es/tree";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { ReportingNode } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useOpenChartFile } from "./hooks";

const SOURCE: Record<string, { label: string; color: string }> = {
  manual: { label: "手動", color: "blue" },
  auto: { label: "自動", color: "default" },
};

function title(node: ReportingNode) {
  const e = node.employee;
  const src = node.source ? SOURCE[node.source] : undefined;
  return (
    <Space size={6}>
      <Link to={`/hr/employees/${e.id}`}>{e.name}</Link>
      {e.position_name && <Typography.Text type="secondary">{e.position_name}</Typography.Text>}
      {e.org_unit_path && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {e.org_unit_path}
        </Typography.Text>
      )}
      {e.partner && <Tag color="green">直雇用ではない</Tag>}
      {src && <Tag color={src.color}>上長 {src.label}</Tag>}
      {node.warning && <Tag color="red">{node.warning}</Tag>}
    </Space>
  );
}

function toTree(nodes: ReportingNode[]): DataNode[] {
  return nodes.map((n) => ({
    key: n.employee.id,
    title: title(n),
    children: n.children.length ? toTree(n.children) : undefined,
  }));
}

function countNodes(nodes: ReportingNode[]): number {
  return nodes.reduce((n, x) => n + 1 + countNodes(x.children), 0);
}

export function ReportingPage() {
  const api = useApi();
  const openFile = useOpenChartFile();
  const [scope, setScope] = useState<"direct" | "all">("all");

  const query = useQuery({
    queryKey: ["hr", "reporting", scope],
    queryFn: () => api.orgReporting(new URLSearchParams({ scope })),
  });

  const tree = useMemo(() => toTree(query.data?.items ?? []), [query.data]);
  const total = countNodes(query.data?.items ?? []);

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          指揮系統図
        </Typography.Title>
        <Space>
          <Button onClick={() => void openFile(`/api/hr/org/reporting.svg?scope=${scope}`)}>
            印刷用SVG(別タブ)
          </Button>
          <Button
            onClick={() => void openFile(`/api/hr/org/chart.mmd?layer=reporting&scope=${scope}`)}
          >
            Mermaid定義
          </Button>
        </Space>
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex gap={12} align="center" wrap>
          <Segmented<"direct" | "all">
            value={scope}
            onChange={(v) => setScope(v)}
            options={[
              { value: "all", label: "全員(協力会社を含む)" },
              { value: "direct", label: "直雇用だけ" },
            ]}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            上長の手動指定が無い人は、所属をさかのぼって一番近い上位役職の人に繋ぐ。
            指定が循環している人は根として出す(その旨を赤で示す)。根 {query.data?.total ?? 0} 名 /
            全 {total} 名。
          </Typography.Text>
        </Flex>
      </Card>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="指揮系統を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        {query.isLoading ? (
          <Flex justify="center" style={{ padding: 48 }}>
            <Spin />
          </Flex>
        ) : tree.length === 0 ? (
          <Typography.Text type="secondary">在籍者がまだ無い。</Typography.Text>
        ) : (
          <Tree treeData={tree} defaultExpandAll selectable={false} />
        )}
      </Card>
    </div>
  );
}
